// --- React
import { useCallback, useMemo, useState } from 'react';
// --- Config & Utils
import * as columnDefs from 'datastage/components/AGGrid/config/columns-def';
// --- Hooks
import { useJobsData } from '../../../hooks/useJobsData';

/**
 * Custom hook for Jobs grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the Jobs grid
 * - Fetches sorted jobs data from the API via useJobsData
 * - Prepares column definitions with sorting controls
 *
 * @param {object} params - Optional configuration
 * @param {Array} params.columnHandlers - Handlers to pass to column definitions
 * @returns {object} { setSort, columns }
 */
export function useJobsGridUtils({ columnHandlers = [] } = {}) {
    const { fetchJobs, updateJobs, jobRange } = useJobsData();
    const [sortedColId, setSortedColId] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    // Handle sorting: update state, fetch sorted data, update jobs
    const setSort = useCallback(
        async (colId, direction) => {
            setSortedColId(colId);
            setSortDirection(direction);

            const customSort = [colId, direction.toUpperCase()];
            const data = await fetchJobs(jobRange, null, customSort);

            if (data) {
                updateJobs({
                    jobs: data?.content,
                    totalPages: data?.totalPages,
                    totalItems: data?.totalElements,
                    currentPage: data?.pageable?.pageNumber,
                    jobSort: customSort,
                });
            }
        },
        [fetchJobs, updateJobs, jobRange],
    );

    // Prepare column definitions with sorting controls
    const baseColumns = columnDefs['jobs'](...columnHandlers)['default'];

    const columns = useMemo(
        () =>
            baseColumns.map((col) => ({
                ...col,
                headerComponentParams: {
                    ...col.headerComponentParams,
                    setSort,
                    sortedColId,
                    sortDirection,
                },
            })),
        [setSort, sortedColId, sortDirection, baseColumns],
    );

    return { setSort, columns };
}
